import { useQuery } from '@tanstack/react-query';
import { slotState, SLOT_STATE } from '../lib/slotState';
import { slotsApi, devicesApi, bmPresenceApi, supportApi } from '../lib/api';

/*
 * Every Dashboard composition reads the same data through this hook, so the candidate looks
 * differ only in presentation. Polling keeps data on screen between fetches (placeholderData) -
 * the page must never blank on a background refresh (owner report, 2026-08-21).
 *
 * WHAT THE SPRING SERVER ACTUALLY HAS (verified 2026-08-23 against /uiapi on the scratch
 * instance). The V2 dashboard asked for /stats/overview, /stats/daily and /sessions/active - all
 * 404 on this server; they never existed, the tiles and chart were always zero. Real sources:
 *   GET /slots        [{ id, slot_id, slot_name, slot_number, slot_label, slot_active, status,
 *                         device_id, ig_username, timezone }]
 *   GET /devices      [{ id, device_id, device_number, device_name, label, platform_name,
 *                         platform_version, locked, lock_owner_slot_id }]
 *   GET /bm/presence  { clients:[{ installId, label, hostname, client_connected, lastSeen,
 *                         devices:[{ serial, model?, state? }], workers:[{ slotName?, ... }] }] }
 *   GET /support/info { uiEnabled, openTickets, ... }
 * There is NO activity time series and NO session list on this server. Do not invent one.
 */
export default function useDashboardData() {
  const slotsQ = useQuery({
    queryKey: ['slots'],
    queryFn: () => slotsApi.list().then((r) => r.data),
    refetchInterval: 30000,
    placeholderData: (prev) => prev,
  });
  const devicesQ = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list().then((r) => r.data),
    refetchInterval: 30000,
    placeholderData: (prev) => prev,
  });
  const presenceQ = useQuery({
    queryKey: ['bmPresence'],
    queryFn: () => bmPresenceApi.list().then((r) => r.data),
    refetchInterval: 10000,
    placeholderData: (prev) => prev,
    retry: 1,
  });
  const supportQ = useQuery({
    queryKey: ['supportInfo'],
    queryFn: () => supportApi.info().then((r) => r.data),
    refetchInterval: 60000,
    placeholderData: (prev) => prev,
    retry: 0,
  });

  const slots = Array.isArray(slotsQ.data) ? slotsQ.data : [];
  const devices = Array.isArray(devicesQ.data) ? devicesQ.data : [];
  const clients = presenceQ.data?.clients || [];
  const support = supportQ.data || null;

  // Three states, one source of truth (lib/slotState). "active" must EXCLUDE paused: a paused slot
  // is not doing work, and counting it would make this headline disagree with the Slots page.
  const activeSlots = slots.filter((s) => slotState(s) === SLOT_STATE.RUNNING);
  const pausedSlots = slots.filter((s) => slotState(s) === SLOT_STATE.PAUSED);
  const inactiveSlots = slots.filter((s) => slotState(s) === SLOT_STATE.NOT_PROVISIONED);
  const slotsWithAccount = slots.filter((s) => s.ig_username);
  const devicesInUse = devices.filter((d) => d.locked);
  const clientsOnline = clients.filter((c) => c.client_connected);
  const phonesConnected = clientsOnline.reduce((n, c) => n + (c.devices?.length || 0), 0);
  const workersRunning = clientsOnline.reduce((n, c) => n + (c.workers?.length || 0), 0);

  // slots grouped by the device they are assigned to (device_id -> slots[])
  const slotsByDevice = {};
  slots.forEach((s) => {
    const k = s.device_id || 'unassigned';
    (slotsByDevice[k] = slotsByDevice[k] || []).push(s);
  });

  // time-zone spread is real and mildly interesting; an activity series is not available
  const timezones = Array.from(new Set(slots.map((s) => s.timezone).filter(Boolean)));

  return {
    // raw
    slots, devices, clients, support,
    // derived, all real
    activeSlots,
    pausedSlots, inactiveSlots, slotsWithAccount, devicesInUse, clientsOnline, phonesConnected,
    workersRunning, slotsByDevice, timezones,
    totalSlots: slots.length,
    totalDevices: devices.length,
    totalClients: clients.length,
    openTickets: support?.uiEnabled ? (support.openTickets ?? 0) : null,   // null = the queue is off
    // state
    firstLoad: slotsQ.data === undefined && devicesQ.data === undefined,
    refreshing: slotsQ.isFetching || devicesQ.isFetching || presenceQ.isFetching,
    error: slotsQ.error || devicesQ.error,
    presenceError: presenceQ.error,
    refetch: () => { slotsQ.refetch(); devicesQ.refetch(); presenceQ.refetch(); supportQ.refetch(); },
  };
}
